import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteSelectableMaterialMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError, logError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {CreateSelectableMaterial} from './components/CreateSelectableMaterial';
import {CREATE_SELECTABLE_MATERIAL_DIALOG} from './constants/CreateSelectableMaterialDialog';

export function WorkshopSelectableMaterials() {
  const navigate = useNavigate();

  const [deleteSelectableMaterial] = useDeleteSelectableMaterialMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, deselectAll, refreshData}) => {
    match(actionKey)
      .with('redirectDetail', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(id)) {
          return logError('id is not defined');
        }

        navigate(
          composePath(settingsRoutes.workshopSelectableMaterialsDetail, {
            params: {id},
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        if (isNil(id)) {
          return logError('id is not defined');
        }

        openDeleteDialog({
          onConfirm: () => {
            deleteSelectableMaterial({selectableMaterialId: id})
              .unwrap()
              .then(deselectAll)
              .then(() => refreshData())
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={i18n.t('page.settings.labels.selectableMaterials')} />
      <SettingsTemplate
        header={{
          title: i18n.t('page.settings.labels.selectableMaterials'),
          actions: [
            {
              type: 'button',
              title: i18n.t('page.settings.actions.newSelectableMaterial'),
              onClick: () =>
                openDialog(<CreateSelectableMaterial />, {
                  id: CREATE_SELECTABLE_MATERIAL_DIALOG,
                  title: i18n.t('page.settings.actions.newSelectableMaterial'),
                  size: 'small',
                }),
            },
          ],
        }}
        description={i18n.t('page.settings.labels.selectableMaterialsDescription')}
        data-testid={testIds.settings.workshopSelectableMaterials('template')}
      >
        <DmsDataGrid gridCode="selectable-materials" actionCallback={actionCallback} />
      </SettingsTemplate>
    </>
  );
}
